import { Boxes, LayoutDashboard, Store, User, Wallet as WalletIcon, WalletCards } from 'lucide-react'
import { AdminDashboard } from 'pages/admin-dashboard'
import { AdminLots, AdminLotDetail } from 'pages/admin-lots'
import { TopupsQueue } from 'pages/admin-topups'
import { CreateLot } from 'pages/admin-create-lot'
import { EditLot } from 'pages/admin-edit-lot'
import { Clients, ClientDetail } from 'pages/admin-clients'
import { AdminSettings } from 'pages/admin-settings'
import { Feed } from 'pages/feed'
import { Lot } from 'pages/lot'
import { Orders } from 'pages/orders'
import { Profile } from 'pages/profile'
import { Wallet } from 'pages/wallet'
import { type JSX } from 'react'
import type { RouteObject } from 'react-router'
import type { TranslationPaths } from 'shared/lib/i18n'
import type { AdminPermission, Role, User as AuthUser } from 'shared/store/auth'
import { hasAdminPermission } from 'shared/lib/permissions/permissions'

export type RouteGroup = 'shop' | 'admin'

export type Route = RouteObject & {
	title?: TranslationPaths
	icon?: JSX.Element
	path: string
	element: JSX.Element
	permissions?: Role[]
	adminPermissions?: AdminPermission[]
	group?: RouteGroup
}

export function GET_ROUTES() {
	return [
		// Storefront
		{ path: '/', element: <Feed />, title: 'navigation.catalog', icon: <Store />, permissions: ['user', 'admin'], group: 'shop' },
		{ path: '/wallet', element: <Wallet />, title: 'navigation.wallet', icon: <WalletIcon />, permissions: ['user'], group: 'shop' },
		{ path: '/profile', element: <Profile />, title: 'navigation.profile', icon: <User />, permissions: ['user'], group: 'shop' },
		{ path: '/orders', element: <Orders />, permissions: ['user'] },
		{ path: '/lot/:id', element: <Lot />, permissions: ['user', 'admin'] },
		// Admin (namespaced under /admin)
		{ path: '/admin', element: <AdminDashboard />, title: 'navigation.dashboard', icon: <LayoutDashboard />, permissions: ['admin'], group: 'admin' },
		{ path: '/admin/topups', element: <TopupsQueue />, title: 'navigation.topups', icon: <WalletCards />, permissions: ['admin'], adminPermissions: ['finance_admin'], group: 'admin' },
		{ path: '/admin/lots', element: <AdminLots />, title: 'navigation.lots', icon: <Boxes />, permissions: ['admin'], adminPermissions: ['lots_admin', 'seller'], group: 'admin' },
		{ path: '/admin/lots/new', element: <CreateLot />, permissions: ['admin'], adminPermissions: ['lots_admin', 'seller'] },
		{ path: '/admin/lots/:id/edit', element: <EditLot />, permissions: ['admin'], adminPermissions: ['lots_admin', 'seller'] },
		{ path: '/admin/lots/:id', element: <AdminLotDetail />, permissions: ['admin'], adminPermissions: ['lots_admin', 'seller'] },
		{ path: '/admin/clients', element: <Clients />, permissions: ['admin'], adminPermissions: ['finance_admin'], group: 'admin' },
		{ path: '/admin/clients/:id', element: <ClientDetail />, permissions: ['admin'], adminPermissions: ['finance_admin'] },
		{ path: '/admin/settings', element: <AdminSettings />, permissions: ['admin'], group: 'admin' },
	] as const satisfies Route[]
}

export function GET_ROUTES_WITH_PERMISSIONS({
	user,
	isRenderingOnNavbar,
}: {
	user: AuthUser
	isRenderingOnNavbar?: boolean
}) {
	let routes = GET_ROUTES() as Route[]

	if (isRenderingOnNavbar) routes = routes.filter((route) => route.title)

	return routes.filter((route) => {
		if (route.permissions && !route.permissions.includes(user.role)) return false
		if (route.group === 'admin' && !hasAdminPermission(user)) return false
		if (
			route.adminPermissions &&
			!hasAdminPermission(user, ...route.adminPermissions)
		)
			return false
		return true
	})
}
